import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";

const EMPTY = {
  company: "", country: "", product_type: "", estimated_value: 0, source: "", stage: "nou",
  notes: "", contact: { name: "", role: "", email: "", phone: "" },
};

export default function LeadFormDialog({ onCreated, triggerLabel = "Lead nou", triggerTestId = "add-lead-btn" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));

  const save = async () => {
    if (!form.company.trim()) return toast.error("Compania este obligatorie");
    setSaving(true);
    try {
      const { data } = await api.post("/leads", { ...form, estimated_value: Number(form.estimated_value) || 0 });
      if (data.duplicate_of) {
        toast.warning(`Atenție: „${data.duplicate_of.company}" există deja în sistem (${data.duplicate_of.type === "project" ? "proiect" : "lead"}). Verifică pentru a evita o dublură.`, { duration: 8000 });
      } else {
        toast.success("Lead creat — scor AI calculat");
      }
      setOpen(false); setForm(EMPTY); onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={triggerTestId}><Plus size={16} weight="bold" className="mr-1.5" /> {triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Adaugă lead</DialogTitle></DialogHeader>
        <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1">
          <div className="grid gap-1.5"><Label>Companie *</Label><Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="lead-company" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Țară</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Tip produs / rezervor</Label><Input value={form.product_type} onChange={(e) => set("product_type", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Valoare estimată (€)</Label><Input type="number" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="lead-value" /></div>
            <div className="grid gap-1.5">
              <Label>Etapă</Label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Sursă</Label><Input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Website, Recomandare, Târg…" /></div>
          <div className="rounded-lg border border-border p-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Contact</div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Nume" value={form.contact.name} onChange={(e) => setC("name", e.target.value)} />
              <Input placeholder="Funcție" value={form.contact.role} onChange={(e) => setC("role", e.target.value)} />
              <Input placeholder="Email" value={form.contact.email} onChange={(e) => setC("email", e.target.value)} />
              <Input placeholder="Telefon" value={form.contact.phone} onChange={(e) => setC("phone", e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-lead-btn">{saving ? "Se salvează…" : "Salvează"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
